import { Controller } from '@hotwired/stimulus';
import { field, message, role } from '../lib/dom';

const VARIABLE_DEFAULTS = { key: '', value: '', stepKey: '' };
const INPUT_DEFAULTS = {
  key: '', label: '', kind: 'string', required: false, defaultValue: '', options: '', hint: '',
};

export default class extends Controller {
  static targets = [
    'concurrencyKey', 'concurrencyKeyField', 'concurrencyMode', 'description', 'name', 'nameError',
    'inputCount', 'inputTemplate', 'inputsBlank', 'inputsError', 'inputsList',
    'variableCount', 'variableTemplate', 'variablesBlank', 'variablesError', 'variablesList',
  ];

  static values = { labels: Object };

  initialize() {
    this.counter = 0;
  }

  render(workflow, errors) {
    this.workflow = workflow;

    this.#fillWorkflow();
    this.#renderVariables();
    this.#renderInputs();
    this.showErrors(errors);
  }

  showErrors(errors) {
    message(this.nameErrorTarget, errors.name);
    message(this.variablesErrorTarget, errors.variables);
    message(this.inputsErrorTarget, errors.inputs);
  }

  changeWorkflow({ currentTarget }) {
    const mode = this.concurrencyModeTarget.value;

    this.workflow.name = this.nameTarget.value;
    this.workflow.description = this.descriptionTarget.value;
    this.workflow.concurrency = { mode, key: this.concurrencyKeyTarget.value };
    this.concurrencyKeyFieldTarget.hidden = mode === 'off';

    this.#announce(`workflow:${currentTarget.dataset.workflowSettingsTarget}`);
  }

  addVariable() {
    this.workflow.variables.push({ id: this.#nextId('v'), ...VARIABLE_DEFAULTS });

    this.#renderVariables();
    this.#announce();
  }

  changeVariable({ currentTarget }) {
    const row = currentTarget.closest('[data-variable-id]');
    const variable = this.workflow.variables.find((candidate) => candidate.id === row.dataset.variableId);

    variable.key = field(row, 'key').value.toUpperCase();
    variable.value = field(row, 'value').value;
    variable.stepKey = field(row, 'stepKey').value;
    field(row, 'key').value = variable.key;

    this.#announce(`${variable.id}:${currentTarget.dataset.field}`);
  }

  deleteVariable({ currentTarget }) {
    const id = currentTarget.closest('[data-variable-id]').dataset.variableId;

    this.workflow.variables = this.workflow.variables.filter((variable) => variable.id !== id);

    this.#renderVariables();
    this.#announce();
  }

  addInput() {
    this.workflow.inputs.push({ id: this.#nextId('i'), ...INPUT_DEFAULTS });

    this.#renderInputs();
    this.#announce();
  }

  changeInput({ currentTarget }) {
    const row = currentTarget.closest('[data-input-id]');
    const input = this.workflow.inputs.find((candidate) => candidate.id === row.dataset.inputId);

    input.key = field(row, 'key').value;
    input.label = field(row, 'label').value;
    input.kind = field(row, 'kind').value;
    input.required = field(row, 'required').checked;
    input.defaultValue = field(row, 'defaultValue').value;
    input.options = field(row, 'options').value;
    input.hint = field(row, 'hint').value;

    role(row, 'options-field').hidden = input.kind !== 'select';

    this.#announce(`${input.id}:${currentTarget.dataset.field}`);
  }

  deleteInput({ currentTarget }) {
    const id = currentTarget.closest('[data-input-id]').dataset.inputId;

    this.workflow.inputs = this.workflow.inputs.filter((input) => input.id !== id);

    this.#renderInputs();
    this.#announce();
  }

  #fillWorkflow() {
    const { concurrency } = this.workflow;

    this.nameTarget.value = this.workflow.name;
    this.descriptionTarget.value = this.workflow.description;
    this.concurrencyModeTarget.value = concurrency.mode;
    this.concurrencyKeyTarget.value = concurrency.key;
    this.concurrencyKeyFieldTarget.hidden = concurrency.mode === 'off';
  }

  #renderVariables() {
    const rows = this.workflow.variables.map((variable) => this.#variableRow(variable));

    this.variablesListTarget.replaceChildren(...rows);
    this.variablesBlankTarget.hidden = this.workflow.variables.length > 0;
    this.variableCountTarget.textContent = this.workflow.variables.length;
  }

  #variableRow(variable) {
    const node = this.variableTemplateTarget.content.firstElementChild.cloneNode(true);
    const value = this.labelsValue.variableValue.replace('%{key}', variable.key);

    node.dataset.variableId = variable.id;
    field(node, 'key').value = variable.key;
    field(node, 'value').value = variable.value;
    field(node, 'stepKey').value = variable.stepKey;
    field(node, 'value').setAttribute('aria-label', value);
    this.#label(node, this.labelsValue.deleteVariable.replace('%{key}', variable.key));

    return node;
  }

  #renderInputs() {
    const rows = this.workflow.inputs.map((input) => this.#inputRow(input));

    this.inputsListTarget.replaceChildren(...rows);
    this.inputsBlankTarget.hidden = this.workflow.inputs.length > 0;
    this.inputCountTarget.textContent = this.workflow.inputs.length;
  }

  #inputRow(input) {
    const node = this.inputTemplateTarget.content.firstElementChild.cloneNode(true);

    node.dataset.inputId = input.id;
    field(node, 'key').value = input.key;
    field(node, 'label').value = input.label;
    field(node, 'kind').value = input.kind;
    field(node, 'required').checked = input.required;
    field(node, 'defaultValue').value = input.defaultValue;
    field(node, 'options').value = input.options;
    field(node, 'hint').value = input.hint;
    role(node, 'options-field').hidden = input.kind !== 'select';
    this.#label(node, this.labelsValue.deleteInput.replace('%{key}', input.key));

    return node;
  }

  #label(node, text) {
    role(node, 'delete').title = text;
    role(node, 'delete').setAttribute('aria-label', text);
  }

  #announce(key = null) {
    this.dispatch('changed', { detail: { key } });
  }

  #nextId(prefix) {
    this.counter += 1;

    return `new-${prefix}${this.counter}`;
  }
}
